import { ClassNames, css } from '@emotion/core'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import React from 'react'
import Modal from 'react-modal'

import { breakpoints } from '../../constants/breakPoints'
import { isDesktop } from '../../helpers/common'
import { dateSerializer, useLocalStorage } from '../../hooks/useLocalStorage'
import { DemoButton } from '../Button'
import { HubspotForm } from '../HubspotForm'
import Image from '../Image'

interface ExitIntentPopupProps {
  title?: string
  description?: string
  hubspotFormId?: string
  customButton?: React.ReactNode
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
  title = 'Before you go 🤚',
  description = 'Join thousands of product experts who enjoy our newsletter and get the best product management practices straight to your inbox.',
  hubspotFormId,
  customButton,
}) => {
  if (!isDesktop()) {
    return null
  }

  const [showPopup, setShowPopup] = React.useState(false)

  const [userId] = useLocalStorage('ajs_user_id', undefined)
  const [seenAt, setSeenAt] = useLocalStorage(
    (!hubspotFormId && 'demo_exit_popup') ||
      (hubspotFormId === '7cd8c2f7-7dc1-498c-9184-29539d228229' && 'newsletter_exit_popup') ||
      (hubspotFormId === 'baa840fc-cd68-4801-a5fe-b25b0df16602' && 'product_digest_exit_popup') ||
      (hubspotFormId === 'a9d8356e-93d1-4067-a2a3-d18f5995d43a' && 'ebook_exit_popup') ||
      '',
    undefined,
    dateSerializer
  )
  const [demoBooked] = useLocalStorage('demo_booked', undefined, dateSerializer)
  const [blogSubscribed] = useLocalStorage('blog_subscribed', false)
  const [productDigestSubscribed] = useLocalStorage('product_digest_subscribed', false)
  const [webinarRegistered] = useLocalStorage('webinar_registered', undefined, dateSerializer)

  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        event.clientY <= 0 &&
        !event.relatedTarget &&
        isDesktop() &&
        !userId &&
        (!seenAt || dayjs().diff(seenAt, 'day') > 0) &&
        ((!hubspotFormId && (!demoBooked || dayjs().diff(demoBooked, 'day') > 30)) ||
          (hubspotFormId === '7cd8c2f7-7dc1-498c-9184-29539d228229' && !blogSubscribed) ||
          (hubspotFormId === 'baa840fc-cd68-4801-a5fe-b25b0df16602' && !productDigestSubscribed) ||
          (hubspotFormId === 'a9d8356e-93d1-4067-a2a3-d18f5995d43a' &&
            (!webinarRegistered || dayjs().diff(webinarRegistered, 'day') > 30)))
      ) {
        setShowPopup(true)
        setSeenAt(dayjs())
        document.body.style.overflowY = 'hidden'
      }
    }
    // used mouseout, because mouseleave doesn't fire in Safari
    document.addEventListener('mouseout', handler)
    return () => document.removeEventListener('mouseout', handler)
  }, [seenAt, setShowPopup, demoBooked, blogSubscribed, productDigestSubscribed, webinarRegistered])

  React.useEffect(() => {
    return () => {
      document.body.style.overflowY = 'auto'
    }
  }, [])

  const closePopup = () => {
    setShowPopup(false)
    document.body.style.overflowY = 'auto'
  }

  return (
    <React.Fragment>
      <ClassNames>
        {({ css }) => {
          return (
            <Modal
              isOpen={showPopup}
              className={css`
                ${modalStyle}
              `}
              overlayClassName={css`
                ${overlayStyle}
              `}
              ariaHideApp={false}
              onRequestClose={closePopup}
              shouldFocusAfterRender={false}
              shouldCloseOnOverlayClick={false}
              shouldCloseOnEsc={false}
              closeTimeoutMS={400}
            >
              <div css={modalContainerStyle}>
                <div css={closeButtonStyle} onClick={closePopup} />
                <div
                  css={[
                    popupBodyStyle,
                    !!hubspotFormId && modalContainerWithButtonStyle,
                    hubspotFormId === 'a9d8356e-93d1-4067-a2a3-d18f5995d43a' && ebookFormStyle,
                  ]}
                >
                  {!hubspotFormId && (
                    <div>
                      <Image
                        src="coins/airfocus-coin.webp"
                        css={css`
                          margin-bottom: -1.5rem;
                        `}
                        alt="airfocus coin"
                      />
                    </div>
                  )}
                  <div>
                    <h2 className="heading2">{parse(title)}</h2>
                    <div className="subtitle">{parse(description)}</div>
                    {customButton && customButton}
                    {!customButton && (
                      <div css={hubspotContainerStyle}>
                        {hubspotFormId ? <HubspotForm hubspotFormId={hubspotFormId} /> : <DemoButton type="primary" />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Modal>
          )
        }}
      </ClassNames>
    </React.Fragment>
  )
}

export default ExitIntentPopup

const modalContainerStyle = css`
  color: #000;
  .heading2 {
    margin-bottom: 0;
  }

  .heading4 {
    margin-bottom: 2rem;
  }

  .subtitle {
    margin-top: 0.5rem;
  }
`

const popupBodyStyle = css`
  text-align: center;
`

const modalContainerWithButtonStyle = css`
  text-align: center;
  .modal-title {
    padding-bottom: 30px;
  }
`

const hubspotContainerStyle = css`
  form {
    margin-top: 1.75rem;
    margin-bottom: 0;
    input {
      width: 100%;
    }
  }

  a {
    margin-top: 1rem;
    width: 100%;
  }

  .submitted-message {
    text-align: center;
    margin-bottom: 3rem;
    p {
      font-size: 1.125rem !important;
      font-weight: 700;
      line-height: 1.5rem;
    }
  }
  .legal-consent-container {
    font-size: 12px !important;
  }

  &:first-child {
    margin: 2rem 0;
  }

  .hs-email {
    text-align: left;
  }
`

const ebookFormStyle = css`
  form {
    grid-gap: 0rem;
    margin-top: 0 !important;
  }
  .hs-richtext {
    margin: 0.5rem 0;
  }
`

const overlayStyle = `
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0;
  z-index: 40;
  background-image: url("/images/exit-intent-background-mesh.webp"), linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8));
  background-position: center, center;
  background-repeat: no-repeat, no-repeat;
  background-size: cover, cover;
  backdrop-filter: blur(8px);
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.small}px) {
    background-image: url("/images/exit-intent-background-mesh.webp"), linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6));
  }
`
const modalStyle = `
  overflow: hidden;
  pointer-events: auto;
  position: absolute;
  background-color: rgb(255, 255, 255);
  border-radius: 0.5rem;
  max-height: max-content;
  max-width: 400px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 50px 0px;
  padding: 3rem 2rem;
  width: 100%;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  z-index: 40;
  
  @media screen and (max-width: ${breakpoints.small}px) {
    max-width: 93%;
  }
`

export const closeButtonStyle = css`
  position: absolute;
  z-index: 1;
  top: 10px;
  right: 10px;
  display: flex;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  &:before,
  &:after {
    position: absolute;
    content: '';
    z-index: 1;
    width: 1px;
    height: calc(100% - 9px);
    background-color: rgba(0, 0, 0, 0.5);
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
  &:hover {
    &:before,
    &:after {
      background-color: rgba(0, 0, 0, 0.8);
    }
  }
`
